import type { ReactNode } from 'react';

import { useMemo } from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import Tooltip from '../Tooltip/Tooltip';

export type DateContainerDate = string | Date | null;

const formatDate = (date?: DateContainerDate, dateFormat?: string) => {
  if (date && !dateFormat) {
    return moment(date).fromNow();
  } else if (date && dateFormat) {
    return moment(date).format(dateFormat);
  } else {
    return '-';
  }
};

export interface DateContainerProps {
  /**
   * Date to render
   */
  date?: DateContainerDate;
  /**
   * Date format to use
   */
  dateFormat?: string;
  /**
   * Icon to show before date
   */
  icon?: ReactNode;
  /**
   * Text to show before date and after icon
   */
  text?: ReactNode;
  /**
   * If date should be semibold
   */
  isBold?: boolean;
  /**
   * Test id for date container
   */
  testId?: string;
  /**
   * Additional CSS classes to be applied to the date container
   */
  additionalClasses?: string;
  /**
   * Additional CSS classes to be applied to the text shown before date
   */
  additionalTextClasses?: string;
  /**
   * Additional CSS classes to be applied to the semibold text
   */
  additionalSemiboldTextClasses?: string;
  /**
   * Component in compacted mode. No text, only icon with tooltip.
   */
  isCompact?: boolean;
}

const DateContainer = ({
  date = '',
  dateFormat = '',
  icon = '',
  text = '',
  isBold = true,
  testId = '',
  additionalClasses = '',
  additionalTextClasses = '',
  additionalSemiboldTextClasses = '',
  isCompact = false,
}: DateContainerProps) => {
  const dateElement = useMemo(
    () => formatDate(date, dateFormat),
    [date, dateFormat],
  );

  const titleElement = useMemo(() => {
    if (isCompact) return;
    if (date) {
      return moment(date).format('LLL');
    } else {
      return '-';
    }
  }, [date, isCompact]);

  return (
    <div
      className={twMerge(
        'flex items-center text-slate-900 dark:text-slate-200 max-w-full',
        additionalClasses,
      )}
      title={titleElement}
      {...getTestProps(testId, `container`)}
    >
      {icon && (
        <Tooltip
          tooltip={`${text} ${dateElement}`}
          tooltipPlacement={'leftCenter'}
          phoneTooltipPlacement={'leftCenter'}
          additionalTooltipClasses={twMerge('hidden', isCompact && 'lg:block')}
          {...getTestProps(testId, `tooltip`, 'testId')}
        >
          <div className="w-fit" {...getTestProps(testId, 'icon')}>
            {icon}
          </div>
        </Tooltip>
      )}

      <span
        className={twMerge(isCompact && 'lg:hidden', additionalTextClasses)}
        {...getTestProps(testId, `text`)}
      >
        {text}
      </span>

      <span
        className={twMerge(
          'min-w-0 truncate',
          isCompact && 'lg:hidden',
          isBold && `font-bold mx-1 ${additionalSemiboldTextClasses}`,
        )}
        {...getTestProps(testId, `date-container`)}
      >
        {dateElement}
      </span>
    </div>
  );
};

export default DateContainer;
